import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import RadioCardPicker from "../../pickers/RadioCardPicker";

export type ServerTableRowsMode = "page" | "custom" | "all";

export interface ServerTableRowsValue {
    mode: ServerTableRowsMode
    /** The number typed for Custom; kept while another mode is picked so switching back restores it. */
    custom: number | null
}

export interface ServerTableRowsPickerProps {
    value: ServerTableRowsValue
    onChange: (value: ServerTableRowsValue) => void
    /** Rows on the loaded table page. */
    pageRowCount: number
    /** Rows of the whole filtered list. */
    totalRowCount: number
    /** The most rows the caller takes. A list over it cannot pick All, and Custom stops there. */
    max?: number
    /** Shown under the number field; the caller decides what makes Custom invalid. */
    error?: string
    /** A line under the cards, such as where to go for more rows than `max`. */
    caption?: ReactNode
}

/** The largest Custom count: the list's total, under the caller's cap. */
export const serverTableRowsCustomMax = (totalRowCount: number, max?: number) =>
    Math.min(totalRowCount, max ?? Infinity);

export const isServerTableRowsCustomValid = (custom: number | null): custom is number =>
    custom != null && Number.isInteger(custom) && custom >= 1;

/**
 * Which rows of a list an action takes: the loaded page, a number from the top, or all of them. The caller owns the
 * value, the fetching and the validation.
 */
export function ServerTableRowsPicker({
    value,
    onChange,
    pageRowCount,
    totalRowCount,
    max,
    error,
    caption
}: ServerTableRowsPickerProps) {
    const {t} = useTranslation();
    const overCap = max != null && totalRowCount > max;

    return (
        <Stack gap={1.5}>
            <Stack gap={1}>
                <Typography variant="body2" color="text.secondary">{t("Rows")}</Typography>
                <RadioCardPicker<ServerTableRowsMode>
                    value={value.mode}
                    onChange={mode => onChange({...value, mode})}
                    ariaLabel={t("Rows")}
                    options={[
                        {value: "page", label: t("This page"), description: t("{{count, number}} rows", {count: pageRowCount})},
                        {value: "custom", label: t("Custom"), description: t("Pick a number")},
                        {
                            value: "all",
                            label: t("All"),
                            description: overCap
                                ? t("Over {{cap, number}} rows", {cap: max})
                                : t("{{count, number}} rows", {count: totalRowCount}),
                            disabled: overCap
                        },
                    ]}
                />
                {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
            </Stack>

            {value.mode === "custom" && (
                <Stack gap={0.5}>
                    <CNumberField
                        label={t("Number of rows")}
                        value={value.custom ?? undefined}
                        onChange={custom => onChange({...value, custom})}
                        min={1}
                        max={Math.max(serverTableRowsCustomMax(totalRowCount, max), 1)}
                        step={100}
                        size="small"
                        fullWidth
                    />
                    {error && <FormHelperText error>{error}</FormHelperText>}
                </Stack>
            )}
        </Stack>
    );
}
